import { useEffect, useState } from 'react'
import { inlineInput } from '@/lib/form-styles'
import { fetchRaster } from '../api/calculation-positions.api'
import { Link } from 'react-router-dom'
import { Sparkles, X } from 'lucide-react'
import { erfuelltRecht } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { schaetzeKiLauf, starteKiLauf, type EingebauteFaehigkeit, type KiSchaetzung } from '../api/calculation-ai.api'
import type { CalcFile, CalcPosition } from '../types/calculation.types'

const TEXTE: Record<EingebauteFaehigkeit, { titel: string; text: string; knopf: string; aus: string }> = {
  V1: {
    titel: 'Preise aus dem Angebot lesen',
    text: 'Die KI ordnet die Angebotszeilen den Kostenzeilen dieser Kalkulation zu. Die Preise kommen als Vorschläge — du übernimmst sie mit Häkchen.',
    knopf: 'Jetzt mit Symphenia lesen',
    aus: '„Preise aus Angeboten"',
  },
  V2: {
    titel: 'Material aus Stückliste (PDF)',
    text: 'Die KI liest die Stückliste und schlägt je Zeile eine Kostenzeile mit Rechenweg und Fundstelle vor — ohne Preise. Übernommen wird in der Vorschlagsliste.',
    knopf: 'Jetzt mit Symphenia lesen',
    aus: '„Stücklisten"',
  },
  CHECK: {
    titel: 'Prüfung vor der Abgabe',
    text: 'Die KI prüft Langtexte, Vorbemerkungen und Befunde gegen die Blätter: vergessene Kosten, Widersprüche, Mengen. Dazu laufen Regeln ohne KI (Ausreißer, Angebote, Preise). Die Prüfung warnt, sie sperrt nichts.',
    knopf: 'Jetzt mit Symphenia prüfen',
    aus: '„Prüfung vor der Abgabe"',
  },
  V6: {
    titel: 'Bieterlücken vorschlagen',
    text: 'Die KI beantwortet die offenen Lücken nur aus Artikeln des Blatts, Befunden und früheren Antworten des Betriebs — ohne Quelle kein Fabrikat. Die Antworten kommen als Vorschläge.',
    knopf: 'Jetzt mit Symphenia vorschlagen',
    aus: '„Bieterlücken"',
  },
}

const zahl = (n: number) => n.toLocaleString('de-DE')
const ANBIETER: Record<string, string> = { ANTHROPIC: 'Claude (Anthropic)', OPENAI: 'OpenAI' }

/**
 * AP-37 Detailblatt N 15. (N1) — **vor jedem Lauf der Dialog** mit Dateien, Umfang, Schätzung und
 * Anbieter. V1 „Preise lesen": das Angebot geht an den KI-Anbieter, die Preise kommen als
 * Vorschläge in die Liste — nichts gelangt ohne Häkchen in die Kalkulation. Ohne Einwilligung oder
 * bei ausgeschalteter Fähigkeit kein Knopf, sondern der Hinweis für den, der es einstellen darf.
 */
export function KiLaufDialog({
  kalkulationId,
  faehigkeit,
  datei,
  onClose,
  onGestartet,
}: {
  kalkulationId: string
  faehigkeit: EingebauteFaehigkeit
  /** V1: das Angebot */
  datei?: CalcFile | null
  onClose: () => void
  onGestartet: () => void
}) {
  const t = TEXTE[faehigkeit]
  // V2: Zielposition — leer heißt „passend vorschlagen" (nach der Positionskennung der Liste).
  const [positionen, setPositionen] = useState<CalcPosition[]>([])
  const [ziel, setZiel] = useState('')
  useEffect(() => {
    if (faehigkeit !== 'V2') return
    void fetchRaster(kalkulationId)
      .then((r) => setPositionen(r.positions.filter((p) => p.kind !== 'TEXT')))
      .catch(() => setPositionen([]))
  }, [faehigkeit, kalkulationId])
  const auftrag = { capability: faehigkeit, ...(datei ? { fileIds: [datei.id] } : {}), ...(faehigkeit === 'V2' && ziel ? { positionIds: [ziel] } : {}) }
  const { user } = useAuthStore()
  const darfEinstellen = erfuelltRecht('calculation.settings.manage', user?.permissions)
  const [s, setS] = useState<KiSchaetzung | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  useEffect(() => {
    void schaetzeKiLauf(kalkulationId, { capability: faehigkeit, ...(datei ? { fileIds: [datei.id] } : {}) })
      .then(setS)
      .catch((e) => setFehler(apiErrorText(e, 'Die Schätzung ließ sich nicht berechnen.')))
  }, [kalkulationId, faehigkeit, datei])

  async function starten() {
    setLaeuft(true)
    setFehler(null)
    try {
      await starteKiLauf(kalkulationId, auftrag)
      onGestartet()
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Lauf ließ sich nicht starten.'))
      setLaeuft(false)
    }
  }

  const leer = faehigkeit === 'V1' ? s?.costLines === 0 : faehigkeit === 'V6' ? s?.gaps === 0 : faehigkeit === 'V2' ? false : s?.positions === 0
  const gesperrt = s && (!s.consentCurrent || !s.capabilityOn || (s.keySource === 'CENTRAL' && s.quota.exhausted) || leer)
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-full max-w-lg rounded-lg border border-border p-5 shadow-xl" style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}>
        <div className="flex items-start justify-between gap-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold">
            <Sparkles className="h-5 w-5 text-primary" /> {t.titel}
          </h2>
          <button type="button" className="text-muted-foreground" onClick={onClose} aria-label="Schließen">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mt-2 text-sm text-muted-foreground">{t.text}</p>
        {!s && !fehler && <p className="mt-4 text-sm text-muted-foreground">Schätzt …</p>}
        {s && (
          <dl className="mt-4 grid grid-cols-[9rem_1fr] gap-x-3 gap-y-1.5 text-sm">
            {s.files.length > 0 && <dt className="text-muted-foreground">Datei</dt>}
            <dd className={s.files.length ? '' : 'hidden'}>
              {s.files.map((f) => (
                <span key={f.id} className="block">
                  {f.name}
                  <span className="text-muted-foreground"> · {f.pages ?? '?'} {f.pages === 1 ? 'Seite' : 'Seiten'} · {f.text ? 'als Text' : 'als Bild (ohne Textschicht)'}</span>
                </span>
              ))}
            </dd>
            <dt className="text-muted-foreground">Umfang</dt>
            <dd>
              {faehigkeit === 'V2' ? (
                <select className={`${inlineInput} !w-full`} value={ziel} onChange={(e) => setZiel(e.target.value)} aria-label="Zielposition">
                  <option value="">Positionen passend vorschlagen</option>
                  {positionen.map((p) => (
                    <option key={p.id} value={p.id}>
                      alles in Pos. {`${p.displayNumber} ${p.shortText}`.trim()}
                    </option>
                  ))}
                </select>
              ) : faehigkeit === 'V1' ? `${s.costLines} Kostenzeilen` : faehigkeit === 'V6' ? `${s.gaps} offene ${s.gaps === 1 ? 'Lücke' : 'Lücken'}` : `${s.positions ?? 0} ${s.positions === 1 ? 'Position' : 'Positionen'}`}
            </dd>
            <dt className="text-muted-foreground">Schätzung</dt>
            <dd>
              ca. {zahl(s.inputTokens + s.outputTokens)} Tokens
              <span className="text-muted-foreground"> ({zahl(s.inputTokens)} hin, bis {zahl(s.outputTokens)} zurück)</span>
            </dd>
            <dt className="text-muted-foreground">Anbieter</dt>
            <dd>
              {ANBIETER[s.provider] ?? s.provider}
              {s.model && <span className="text-muted-foreground"> · {s.model}</span>}
            </dd>
            <dt className="text-muted-foreground">Bezahlt über</dt>
            <dd>
              {s.keySource === 'TENANT' ? (
                'eigenen Schlüssel des Betriebs'
              ) : (
                <>
                  Kontingent von Symphenia
                  <span className={s.quota.warn ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}>
                    {' '}
                    · {zahl(s.quota.used)} von {zahl(s.quota.limit)} verbraucht
                  </span>
                </>
              )}
            </dd>
          </dl>
        )}
        {s && gesperrt && (
          <p className="mt-4 rounded-md bg-amber-500/15 px-3 py-2 text-sm text-amber-900 dark:text-amber-200">
            {!s.consentCurrent
              ? 'Für die eingebauten KI-Fähigkeiten fehlt die Einwilligung.'
              : !s.capabilityOn
                ? `${t.aus} ist in den KI-Einstellungen ausgeschaltet.`
                : leer
                  ? faehigkeit === 'V1'
                    ? 'Die Kalkulation hat noch keine Kostenzeilen — Preise brauchen Zeilen, denen sie zugeordnet werden.'
                    : faehigkeit === 'V6'
                      ? 'Keine offene Bieterlücke.'
                      : 'Die Kalkulation hat noch keine Position.'
                  : 'Das KI-Kontingent dieses Monats ist aufgebraucht.'}{' '}
            {!leer &&
              !(s.keySource === 'CENTRAL' && s.quota.exhausted && s.consentCurrent && s.capabilityOn) &&
              (darfEinstellen ? (
                <Link to="/calculations/settings?tab=ki" className="text-primary underline">
                  KI-Einstellungen öffnen
                </Link>
              ) : (
                'Das kann ändern, wer die Einstellungen der Kalkulation ändern darf.'
              ))}
          </p>
        )}
        {fehler && <p className="mt-3 text-sm text-destructive">{fehler}</p>}
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" className="rounded-md border border-border px-3 py-1.5 text-sm" onClick={onClose}>
            Abbrechen
          </button>
          <button type="button" className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50" disabled={!s || !!gesperrt || laeuft} onClick={() => void starten()}>
            <Sparkles className="h-4 w-4" /> {t.knopf}
          </button>
        </div>
      </div>
    </div>
  )
}
